import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaGavel, FaHandshake, FaExclamationTriangle, FaBalanceScale } from 'react-icons/fa';

const TermsOfService = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle scroll-to-top button visibility
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return (
    <div className="legal-page">
      {/* Header */}
      <div className="legal-header">
        <div className="container">
          <Link to="/" className="back-link">
            <FaArrowLeft />
            <span>Back to Home</span>
          </Link>
          
          <div className="legal-title-section">
            <div className="legal-icon">
              <FaGavel />
            </div>
            <h1 className="legal-title">Terms of Service</h1>
            <p className="legal-subtitle">
              The legal agreement for using Passly's decentralized identity platform
            </p>
            <div className="legal-meta">
              <span>Last updated: {new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="legal-content">
        <div className="container">
          <div className="legal-grid">
            
            {/* Table of Contents */}
            <div className="legal-toc">
              <h3>Table of Contents</h3>
              <ul>
                <li><a href="#acceptance">Acceptance of Terms</a></li>
                <li><a href="#service-description">Service Description</a></li>
                <li><a href="#user-obligations">User Obligations</a></li>
                <li><a href="#verification-process">Verification Process</a></li>
                <li><a href="#blockchain-terms">Blockchain Terms</a></li>
                <li><a href="#prohibited-use">Prohibited Use</a></li>
                <li><a href="#intellectual-property">Intellectual Property</a></li>
                <li><a href="#disclaimers">Disclaimers</a></li>
                <li><a href="#limitation-liability">Limitation of Liability</a></li>
                <li><a href="#termination">Termination</a></li>
                <li><a href="#governing-law">Governing Law</a></li>
                <li><a href="#contact">Contact Information</a></li>
              </ul>
            </div>

            {/* Main Content */}
            <div className="legal-main">
              
              <section id="acceptance" className="legal-section">
                <div className="section-header">
                  <FaHandshake className="section-icon" />
                  <h2>Acceptance of Terms</h2>
                </div>
                
                <div className="terms-highlight">
                  <p>
                    By accessing or using Passly's services, you agree to be bound by these Terms of Service 
                    and our Privacy Policy. If you disagree with any part of these terms, you may not access 
                    or use our services.
                  </p>
                </div>

                <p>
                  These terms constitute a legally binding agreement between you and Passly Protocol ("we," "us," or "our"). 
                  You represent that you are at least 18 years old and have the legal capacity to enter into this agreement.
                </p>
              </section>

              <section id="service-description" className="legal-section">
                <h2>Service Description</h2>
                
                <div className="service-overview">
                  <h3>🎯 What Passly Provides</h3>
                  <p>
                    Passly is a decentralized identity verification platform that allows users to:
                  </p>
                  <ul>
                    <li>Create digital identity passports on the blockchain</li>
                    <li>Verify ownership of social media accounts</li>
                    <li>Generate cryptographic proofs of identity</li>
                    <li>Use verified identity across Web3 applications</li>
                  </ul>

                  <h3>🔗 Blockchain Integration</h3>
                  <p>
                    Our service operates on blockchain technology, specifically the Neurith Testnet. 
                    Verification data is permanently recorded on the blockchain and cannot be altered or deleted.
                  </p>

                  <h3>🚧 Beta Service Notice</h3>
                  <div className="beta-notice">
                    <FaExclamationTriangle />
                    <div>
                      <strong>Beta Version:</strong> Passly is currently in beta. The service may contain bugs, 
                      experience downtime, or undergo significant changes. Use at your own discretion.
                    </div>
                  </div>
                </div>
              </section>

              <section id="user-obligations" className="legal-section">
                <h2>User Obligations</h2>
                
                <div className="obligations-grid">
                  <div className="obligation">
                    <h4>✅ Account Accuracy</h4>
                    <p>You must provide accurate information and only verify accounts you legitimately own</p>
                  </div>
                  
                  <div className="obligation">
                    <h4>🔐 Security</h4>
                    <p>You are responsible for maintaining the security of your wallet and private keys</p>
                  </div>
                  
                  <div className="obligation">
                    <h4>📋 Compliance</h4>
                    <p>You must comply with all applicable laws and regulations in your jurisdiction</p>
                  </div>
                  
                  <div className="obligation">
                    <h4>🚫 No Misuse</h4>
                    <p>You must not attempt to circumvent, disable, or interfere with our security features</p>
                  </div>
                </div>

                <h3>Age Requirements</h3>
                <p>
                  You must be at least 18 years old to use Passly. By using our service, you represent 
                  and warrant that you meet this age requirement.
                </p>

                <h3>Wallet Requirements</h3>
                <p>
                  To use Passly, you must have a compatible Web3 wallet and access to the Neurith Testnet. 
                  You are solely responsible for:
                </p>
                <ul>
                  <li>Maintaining the security of your wallet and private keys</li>
                  <li>Any transactions or activities performed with your wallet</li>
                  <li>Ensuring your wallet software is up to date and secure</li>
                </ul>
              </section>

              <section id="verification-process" className="legal-section">
                <h2>Verification Process</h2>
                
                <h3>🔍 How Verification Works</h3>
                <p>
                  Our verification process involves:
                </p>
                <ol>
                  <li>OAuth authentication with social media platforms</li>
                  <li>Cryptographic proof generation</li>
                  <li>On-chain recording of verification status</li>
                  <li>Immediate deletion of personal data</li>
                </ol>

                <h3>📝 User Responsibilities</h3>
                <div className="verification-terms">
                  <ul>
                    <li><strong>Legitimate Ownership:</strong> You may only verify accounts you genuinely own and control</li>
                    <li><strong>Active Accounts:</strong> Accounts must be active and in good standing with their respective platforms</li>
                    <li><strong>No Shared Accounts:</strong> You cannot verify shared, business, or accounts owned by others</li>
                    <li><strong>One Verification Per Account:</strong> Each social media account can only be verified once across all Passly users</li>
                  </ul>
                </div>

                <h3>⚠️ Verification Limitations</h3>
                <div className="limitation-notice">
                  <p>
                    <strong>No Guarantee:</strong> While we strive for accurate verification, we cannot guarantee 
                    the authenticity of all social media accounts or prevent all forms of fraud. Users should 
                    exercise their own judgment when relying on verification status.
                  </p>
                </div>
              </section>

              <section id="blockchain-terms" className="legal-section">
                <h2>Blockchain Terms</h2>
                
                <div className="blockchain-terms">
                  <h3>🔗 Immutable Records</h3>
                  <p>
                    Once verification data is recorded on the blockchain, it becomes permanent and cannot be 
                    modified or deleted. This includes:
                  </p>
                  <ul>
                    <li>Your wallet address</li>
                    <li>Verification timestamps</li>
                    <li>Platform verification status</li>
                    <li>Cryptographic proof hashes</li>
                  </ul>

                  <h3>⛽ Gas Fees</h3>
                  <p>
                    Blockchain transactions may require gas fees. While we cover these costs during beta, 
                    users may be responsible for gas fees in future versions.
                  </p>

                  <h3>🌐 Network Dependency</h3>
                  <p>
                    Our service depends on the Neurith Testnet blockchain. We are not responsible for:
                  </p>
                  <ul>
                    <li>Blockchain network downtime or congestion</li>
                    <li>Changes to blockchain protocols</li>
                    <li>Third-party blockchain infrastructure issues</li>
                  </ul>
                </div>
              </section>

              <section id="prohibited-use" className="legal-section">
                <div className="section-header">
                  <FaExclamationTriangle className="section-icon" />
                  <h2>Prohibited Use</h2>
                </div>
                
                <div className="prohibited-activities">
                  <p>You agree not to:</p>
                  
                  <div className="prohibited-grid">
                    <div className="prohibited-item">
                      <h4>🚫 Fraud & Deception</h4>
                      <ul>
                        <li>Verify accounts you don't own</li>
                        <li>Create fake or misleading identities</li>
                        <li>Impersonate other individuals or entities</li>
                      </ul>
                    </div>
                    
                    <div className="prohibited-item">
                      <h4>⚡ Technical Abuse</h4>
                      <ul>
                        <li>Attempt to hack, disrupt, or damage our systems</li>
                        <li>Use automated tools to interact with our service</li>
                        <li>Reverse engineer our verification algorithms</li>
                      </ul>
                    </div>
                    
                    <div className="prohibited-item">
                      <h4>📜 Legal Violations</h4>
                      <ul>
                        <li>Violate any applicable laws or regulations</li>
                        <li>Infringe on intellectual property rights</li>
                        <li>Engage in money laundering or fraud</li>
                      </ul>
                    </div>
                    
                    <div className="prohibited-item">
                      <h4>🔄 System Abuse</h4>
                      <ul>
                        <li>Create multiple accounts to circumvent limitations</li>
                        <li>Sell or transfer verification credentials</li>
                        <li>Use our service for commercial purposes without permission</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              <section id="intellectual-property" className="legal-section">
                <h2>Intellectual Property</h2>
                
                <h3>🏢 Our Rights</h3>
                <p>
                  Passly and all related trademarks, logos, and intellectual property are owned by us or our licensors. 
                  You may not use our intellectual property without express written permission.
                </p>

                <h3>👤 Your Rights</h3>
                <p>
                  You retain all rights to your personal information and social media accounts. By using our service, 
                  you grant us a limited license to:
                </p>
                <ul>
                  <li>Process your social media data for verification purposes</li>
                  <li>Generate and store cryptographic proofs</li>
                  <li>Display your verification status on the blockchain</li>
                </ul>

                <h3>🔓 Open Source</h3>
                <p>
                  Certain components of Passly may be released under open source licenses. 
                  Such components are governed by their respective license terms.
                </p>
              </section>

              <section id="disclaimers" className="legal-section">
                <div className="section-header">
                  <FaBalanceScale className="section-icon" />
                  <h2>Disclaimers</h2>
                </div>
                
                <div className="disclaimer-notice">
                  <h3>⚠️ Service Provided "As Is"</h3>
                  <p>
                    <strong>PASSLY IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.</strong> We disclaim all warranties, 
                    whether express, implied, or statutory, including but not limited to:
                  </p>
                  <ul>
                    <li>Merchantability and fitness for a particular purpose</li>
                    <li>Non-infringement of third-party rights</li>
                    <li>Uninterrupted or error-free operation</li>
                    <li>Accuracy or completeness of verification data</li>
                  </ul>
                </div>

                <h3>🌐 Third-Party Services</h3>
                <p>
                  Our service integrates with third-party platforms (Twitter, GitHub, etc.). We are not responsible 
                  for the availability, accuracy, or reliability of these third-party services.
                </p>

                <h3>🔒 Security Disclaimers</h3>
                <p>
                  While we implement security best practices, we cannot guarantee absolute security. 
                  You acknowledge the inherent risks of blockchain and cryptocurrency technologies.
                </p>
              </section>

              <section id="limitation-liability" className="legal-section">
                <h2>Limitation of Liability</h2>
                
                <div className="liability-notice">
                  <p>
                    <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, PASSLY'S LIABILITY IS LIMITED AS FOLLOWS:</strong>
                  </p>
                  
                  <div className="liability-items">
                    <div className="liability-item">
                      <h4>💰 Monetary Damages</h4>
                      <p>Our total liability shall not exceed $100 USD or the amount you paid us in the last 12 months, whichever is greater</p>
                    </div>
                    
                    <div className="liability-item">
                      <h4>❌ Excluded Damages</h4>
                      <p>We are not liable for indirect, incidental, special, consequential, or punitive damages</p>
                    </div>
                    
                    <div className="liability-item">
                      <h4>🛡️ User Responsibility</h4>
                      <p>You are responsible for your own wallet security and the consequences of verification decisions</p>
                    </div>
                  </div>
                </div>

                <h3>🚫 Specific Exclusions</h3>
                <p>We are not liable for:</p>
                <ul>
                  <li>Loss of cryptocurrency or digital assets</li>
                  <li>Damages from wallet compromise or private key loss</li>
                  <li>Third-party platform actions or policy changes</li>
                  <li>Blockchain network issues or failures</li>
                  <li>Regulatory actions or legal complications</li>
                </ul>
              </section>

              <section id="termination" className="legal-section">
                <h2>Termination</h2>
                
                <h3>👤 Your Right to Terminate</h3>
                <p>
                  You may stop using our service at any time. However, blockchain records cannot be deleted 
                  and will remain permanently on the blockchain.
                </p>

                <h3>🏢 Our Right to Terminate</h3>
                <p>We may suspend or terminate your access to our service:</p>
                <ul>
                  <li>For violation of these terms</li>
                  <li>For fraudulent or abusive behavior</li>
                  <li>If required by law or regulation</li>
                  <li>If we discontinue the service</li>
                </ul>

                <h3>📊 Effect of Termination</h3>
                <p>
                  Upon termination:
                </p>
                <ul>
                  <li>Your access to our service will be revoked</li>
                  <li>Blockchain records will remain permanent</li>
                  <li>These terms will continue to apply to past usage</li>
                </ul>
              </section>

              <section id="governing-law" className="legal-section">
                <h2>Governing Law and Disputes</h2>
                
                <h3>⚖️ Governing Law</h3>
                <p>
                  These terms are governed by the laws of [JURISDICTION], without regard to conflict of law principles.
                </p>

                <h3>🤝 Dispute Resolution</h3>
                <p>
                  Any disputes arising from these terms or our service shall be resolved through:
                </p>
                <ol>
                  <li><strong>Direct Communication:</strong> Contact us first to resolve issues informally</li>
                  <li><strong>Mediation:</strong> If informal resolution fails, disputes shall be mediated</li>
                  <li><strong>Arbitration:</strong> Unresolved disputes shall be settled through binding arbitration</li>
                </ol>

                <h3>🚫 Class Action Waiver</h3>
                <p>
                  You agree to resolve disputes individually and waive any right to participate in 
                  class action lawsuits or class-wide arbitrations.
                </p>
              </section>

              <section className="legal-section">
                <h2>Changes to Terms</h2>
                <p>
                  We may update these Terms of Service periodically. Material changes will be communicated 
                  through our website and by updating the "Last Updated" date. Continued use of our service 
                  after changes constitutes acceptance of the updated terms.
                </p>

                <h3>📢 Notification of Changes</h3>
                <p>For significant changes, we will provide at least 30 days notice through:</p>
                <ul>
                  <li>Prominent website notices</li>
                  <li>Email notifications (if you've provided an email)</li>
                  <li>In-app notifications</li>
                </ul>
              </section>

              <section id="contact" className="legal-section">
                <h2>Contact Information</h2>
                
                <div className="contact-info">
                  <p>
                    For questions about these Terms of Service or to report violations:
                  </p>
                  
                  <div className="contact-methods">
                    <div className="contact-method">
                      <strong>📧 Email:</strong>
                      <span>hello@passly.xyz</span>
                    </div>
                    
                    <div className="contact-method">
                      <strong>🌐 Website:</strong>
                      <span>https://passly.xyz</span>
                    </div>
                    
                  </div>

                  <p className="response-time">
                    We aim to respond to all legal inquiries within 5 business days.
                  </p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button 
        className={`return-to-top ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Return to top"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 19V5M5 12l7-7 7 7"/>
        </svg>
      </button>

      <style jsx>{`
        .legal-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
          color: #ffffff;
        }

        .legal-header {
          background: linear-gradient(135deg, rgba(15, 15, 35, 0.95) 0%, rgba(26, 26, 46, 0.95) 50%, rgba(22, 33, 62, 0.95) 100%);
          border-bottom: 1px solid rgba(124, 77, 255, 0.2);
          padding: 2rem 0 4rem 0;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          font-size: 0.9rem;
          margin-bottom: 2rem;
          transition: color 0.3s ease;
        }

        .back-link:hover {
          color: var(--color-primary);
        }

        .legal-title-section {
          text-align: center;
          max-width: 600px;
          margin: 0 auto;
        }

        .legal-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.2) 0%, rgba(0, 204, 184, 0.2) 100%);
          border-radius: 50%;
          margin-bottom: 1.5rem;
          font-size: 2rem;
          color: var(--color-primary);
        }

        .legal-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1rem;
          background: linear-gradient(90deg, #ffffff, rgba(255, 255, 255, 0.8));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .legal-subtitle {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 1.5rem;
        }

        .legal-meta {
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.9rem;
        }

        .legal-content {
          padding: 4rem 0;
        }

        .legal-grid {
          display: grid;
          grid-template-columns: 250px 1fr;
          gap: 3rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .legal-toc {
          position: sticky;
          top: 2rem;
          height: fit-content;
          background: rgba(30, 30, 30, 0.6);
          border-radius: 12px;
          padding: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .legal-toc h3 {
          font-size: 1.1rem;
          margin-bottom: 1rem;
          color: var(--color-primary);
        }

        .legal-toc ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .legal-toc li {
          margin-bottom: 0.5rem;
        }

        .legal-toc a {
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          font-size: 0.9rem;
          transition: color 0.3s ease;
        }

        .legal-toc a:hover {
          color: var(--color-primary);
        }

        .legal-main {
          background: rgba(30, 30, 30, 0.4);
          border-radius: 16px;
          padding: 3rem;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .legal-section {
          margin-bottom: 3rem;
          padding-bottom: 2rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .legal-section:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .section-icon {
          color: var(--color-primary);
          font-size: 1.5rem;
        }

        .legal-section h2 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #ffffff;
        }

        .legal-section h3 {
          font-size: 1.3rem;
          font-weight: 600;
          margin: 2rem 0 1rem 0;
          color: rgba(255, 255, 255, 0.9);
        }

        .legal-section h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 1.5rem 0 0.75rem 0;
          color: rgba(255, 255, 255, 0.85);
        }

        .legal-section p {
          line-height: 1.7;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 1.25rem;
        }

        .legal-section ul, .legal-section ol {
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.7;
          margin-bottom: 1.25rem;
        }

        .legal-section li {
          margin-bottom: 0.5rem;
        }

        .terms-highlight {
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.1) 0%, rgba(0, 204, 184, 0.1) 100%);
          border: 1px solid rgba(124, 77, 255, 0.3);
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .service-overview {
          background: rgba(255, 255, 255, 0.02);
          border-radius: 12px;
          padding: 1.5rem;
          margin: 1.5rem 0;
        }

        .beta-notice {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          background: rgba(255, 193, 7, 0.1);
          border: 1px solid rgba(255, 193, 7, 0.3);
          border-radius: 8px;
          padding: 1.25rem;
          margin: 1.5rem 0;
          color: #ffc107;
        }

        .obligations-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .obligation {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          padding: 1.25rem;
        }

        .obligation h4 {
          margin-top: 0;
          margin-bottom: 0.75rem;
        }

        .obligation p {
          margin-bottom: 0;
          font-size: 0.95rem;
        }

        .verification-terms {
          background: rgba(255, 255, 255, 0.02);
          border-radius: 8px;
          padding: 1.5rem;
          margin: 1.5rem 0;
        }

        .limitation-notice {
          background: rgba(255, 193, 7, 0.1);
          border: 1px solid rgba(255, 193, 7, 0.3);
          border-radius: 8px;
          padding: 1.25rem;
          margin: 1.5rem 0;
        }

        .blockchain-terms {
          background: rgba(255, 255, 255, 0.02);
          border-radius: 12px;
          padding: 1.5rem;
          margin: 1.5rem 0;
        }

        .prohibited-activities {
          margin: 1.5rem 0;
        }

        .prohibited-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .prohibited-item {
          background: rgba(239, 68, 68, 0.05);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 8px;
          padding: 1.25rem;
        }

        .prohibited-item h4 {
          margin-top: 0;
          margin-bottom: 0.75rem;
          color: #ff6b6b;
        }

        .prohibited-item ul {
          margin-bottom: 0;
        }

        .disclaimer-notice {
          background: rgba(255, 193, 7, 0.1);
          border: 1px solid rgba(255, 193, 7, 0.3);
          border-radius: 12px;
          padding: 1.5rem;
          margin: 1.5rem 0;
        }

        .disclaimer-notice h3 {
          color: #ffc107;
          margin-top: 0;
        }

        .liability-notice {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 12px;
          padding: 1.5rem;
          margin: 1.5rem 0;
        }

        .liability-items {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .liability-item {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          padding: 1.25rem;
        }

        .liability-item h4 {
          margin-top: 0;
          margin-bottom: 0.75rem;
        }

        .liability-item p {
          margin-bottom: 0;
          font-size: 0.9rem;
        }

        .contact-info {
          background: rgba(255, 255, 255, 0.02);
          border-radius: 12px;
          padding: 1.5rem;
        }

        .contact-methods {
          margin: 1.5rem 0;
        }

        .contact-method {
          display: flex;
          gap: 1rem;
          margin-bottom: 0.75rem;
          align-items: center;
        }

        .contact-method strong {
          min-width: 80px;
          color: var(--color-primary);
        }

        .response-time {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.6);
          margin-top: 1rem;
        }

        /* Return to Top Button Styles */
        .return-to-top {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          width: 3.5rem;
          height: 3.5rem;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.9) 0%, rgba(0, 204, 184, 0.9) 100%);
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 
            0 4px 15px rgba(0, 0, 0, 0.3),
            0 0 10px rgba(124, 77, 255, 0.4);
          transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          opacity: 0;
          transform: translateY(20px) scale(0.9);
          pointer-events: none;
          z-index: 100;
        }
        
        .return-to-top.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
          pointer-events: all;
        }
        
        .return-to-top:hover {
          transform: translateY(-5px);
          box-shadow: 
            0 6px 20px rgba(0, 0, 0, 0.4),
            0 0 15px rgba(124, 77, 255, 0.5);
        }
        
        .return-to-top:active {
          transform: translateY(0) scale(0.95);
        }
        
        .return-to-top::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        
        .return-to-top:hover::before {
          opacity: 1;
        }
        
        .return-to-top svg {
          width: 1.5rem;
          height: 1.5rem;
          stroke-width: 2.5;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .legal-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .legal-toc {
            position: static;
            order: -1;
          }
        }

        @media (max-width: 768px) {
          .return-to-top {
            bottom: 1.5rem;
            right: 1.5rem;
            width: 3rem;
            height: 3rem;
          }
          
          .return-to-top svg {
            width: 1.25rem;
            height: 1.25rem;
          }

          .legal-header {
            padding: 1.5rem 0 3rem 0;
          }

          .legal-title {
            font-size: 2rem;
          }

          .legal-subtitle {
            font-size: 1.1rem;
          }

          .legal-main {
            padding: 2rem 1.5rem;
          }

          .obligations-grid,
          .prohibited-grid,
          .liability-items {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .return-to-top {
            bottom: 1rem;
            right: 1rem;
            width: 2.75rem;
            height: 2.75rem;
          }

          .legal-content {
            padding: 2rem 0;
          }

          .legal-main {
            padding: 1.5rem 1rem;
          }

          .legal-section {
            margin-bottom: 2rem;
          }

          .legal-section h2 {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default TermsOfService;